import { useState, useEffect, useCallback, useContext } from 'react';

import { IsFormSaved } from '../context/FromStatusContext';

import { SecureGet, SecurePost, SecurePut } from '../services/axiosCall';
import apis from '../services/Apis';
import useEvaluationRequestScope, { scopedEvaluationResults } from './useEvaluationRequestScope';

const useAdminEvaluation = ({
  url,
  formId,
  userDetails,
  selectedYear,
  messageApi,
  convertToTableData,
  convertToRequestData,
  rowUser
}) => {
  const { setIsFormSaved } = useContext(IsFormSaved);

  const scopeKey = JSON.stringify([rowUser, selectedYear, formId, url]);
  const scope = useEvaluationRequestScope(scopeKey);
  const [loadedScope, setLoadedScope] = useState(null);
  const visible = loadedScope === scopeKey;

  // State to store the unique identifier for the plan
  const [id, setId] = useState(null);
  const handleId = (id) => setId(id);

  const [fetchLoading, setFetchLoading] = useState(false);
  const handleFetchLoading = (loading) => setFetchLoading(loading);

  const [updateLoading, setUpdateLoading] = useState(false);
  const handleUpdateLoading = (loading) => setUpdateLoading(loading);

  const calculateLoading = false;


  const [evaluateLoading, setEvaluateLoading] = useState(false);
  const handleEvaluateLoading = (loading) => setEvaluateLoading(loading);

  const [planing, setPlaning] = useState([]);
  const handlePlaning = (plan) => setPlaning(plan);

  const [_planing, _setPlaning] = useState([]);
  const _handlePlaning = (plan) => _setPlaning(plan)

  const [semesterComments, setSemesterComments] = useState(null);
  const [cumulativeApaPoints, setCumulativeApaPoints] = useState(0)

  const [updated, setUpdated] = useState(false);
  const [showEvaluate, setShowEvaluate] = useState(false);

  const [status, setStatus] = useState(0);
  const handleStatus = (st) => setStatus(st);


  // Fetch only the selected faculty/year/form; empty admin results stay empty.
  const fetchPlaning = useCallback((year, requestedForm) => {
    const isCurrent = scope.begin();
    handlePlaning([]);
    setSemesterComments(null);
    setCumulativeApaPoints(0);
    setLoadedScope(scopeKey);
    _handlePlaning([]);
    handleUpdateLoading(false);
    handleEvaluateLoading(false);
    handleId(null);
    handleStatus(0);
    setShowEvaluate(false);
    if (!rowUser || !year || !requestedForm) {
      handleFetchLoading(false);
      return;
    }
    handleFetchLoading(true);
    SecureGet({ url, params: { year, apa_form: requestedForm, user: rowUser, limit: 1000 } })
      .then(async (response) => {
        if (!isCurrent()) return;
        const results = scopedEvaluationResults(response, rowUser, year, requestedForm);
        const record = results[0];
        let json = record?.json;
        // Preserve planning initialization for self evaluation only.
        if (!record && url === apis.EVALUATION) {
          const planning = await SecureGet({
            url: apis.PLANING, params: { year, apa_form: requestedForm },
          });
          if (!isCurrent()) return;
          json = planning?.data?.results?.[0]?.planning;
        }
        const rows = json == null ? [] : (convertToTableData ? convertToTableData(json) : json);
        handlePlaning(rows);
        _handlePlaning(rows);
        handleId(record?.id ?? null);
        handleStatus(record?.status ?? 0);
        setShowEvaluate(Boolean(record));
        setSemesterComments(record?.comment ?? null);
        if (record) calculatePlaning(requestedForm);
      })
      .catch(() => {
        if (!isCurrent()) return;
        handlePlaning([]);
      })
      .finally(() => { if (isCurrent()) handleFetchLoading(false); });
  // Converters are section-level functions. Scope is represented by scopeKey.
  }, [scopeKey, rowUser, url, convertToTableData]);

  const updatePlaning = () => {
    if (!visible || fetchLoading) return;
    const isCurrent = scope.capture();
    if (!isCurrent()) return;
    handleUpdateLoading(true);

    const newPlan = {
      apa_form: formId,
      year: selectedYear,
      json: convertToRequestData ? convertToRequestData(planing) : planing,
      user: userDetails,
    };

    if (id) {
      SecurePut({
        url: `${url}/${id}`,
        data: { ...newPlan },
      })
        .then(() => {
          if (!isCurrent()) return;
          fetchPlaning(selectedYear, formId);
          handleUpdateLoading(false);
          calculatePlaning(formId);
          messageApi.success('Successfully updated');
        })
        .catch((error) => {
          if (!isCurrent()) return;
          handleUpdateLoading(false);
          const { response } = error;
          const { data } = response || {};
          const { error: errorMsg } = data || {};

          if (errorMsg) messageApi.error(errorMsg);
          else messageApi.error("Something went wrong")
        });
    } else {
      SecurePost({
        url: `${url}`,
        data: { ...newPlan },
      })
        .then(() => {
          if (!isCurrent()) return;
          fetchPlaning(selectedYear, formId);
          handleUpdateLoading(false);
          calculatePlaning(formId);
          messageApi.success('Successfully updated');
        })
        .catch((error) => {
          if (!isCurrent()) return;
          handleUpdateLoading(false);
          const { response } = error;
          const { data } = response || {};
          const { error: errorMsg } = data || {};

          if (errorMsg) messageApi.error(errorMsg);
          else messageApi.error("Something went wrong")
        });
    }
  };

  const calculatePlaning = (ID) => {
    if (!ID || !rowUser || !selectedYear) return;
    const isCurrent = scope.capture();
    if (!isCurrent()) return;
    SecureGet({
      url: `${apis.CUMUlATIVE_APA}/${ID}/${selectedYear}`,
      params: { user: rowUser },
    })
      .then((response) => {
        if (isCurrent()) setCumulativeApaPoints(response?.data ?? 0);
      })
      .catch(() => { if (isCurrent()) setCumulativeApaPoints(0); });
  };

  const evaluatePlaning = (selectedStatus, comment) => {
    if (!visible || fetchLoading) return;
    const isCurrent = scope.capture();
    if (!isCurrent()) return;
    handleEvaluateLoading(true);

    const newPlan = {
      id: id,
      status: selectedStatus,
      type: 1,
      comment: comment
    };

    if (id) {
      SecurePost({
        url: `${apis.EVALUATION_FORM}`,
        data: { ...newPlan },
      })
        .then(() => {
          if (!isCurrent()) return;
          fetchPlaning(selectedYear, formId);
          handleEvaluateLoading(false);
          messageApi.success('Evaluation updated successfully');
        })
        .catch((error) => {
          if (!isCurrent()) return;
          handleEvaluateLoading(false);
          const { response } = error;
          const { data } = response || {};
          const { error: errorMsg } = data || {};

          if (errorMsg) messageApi.error(errorMsg);
          else messageApi.error("Something went wrong")
        });
    } else {
      console.warn('[useEvaluation] evaluatePlaning called with no plan id — nothing to evaluate yet');
      handleEvaluateLoading(false);
      messageApi.error('This faculty has not submitted planning for this form/year yet.');
    }
  }


  useEffect(() => {
    fetchPlaning(selectedYear, formId);
  }, [fetchPlaning, formId, selectedYear, rowUser]);

  useEffect(() => {
    if (JSON.stringify(planing) === JSON.stringify(_planing)) {
      setIsFormSaved(true);
      setUpdated(false)
    } else {
      setIsFormSaved(false);
      setUpdated(true)
    };
  }, [_planing, planing]);

  useEffect(() => {
    setIsFormSaved(true)
  }, []);

  return {
    planing: visible ? planing : [],
    updated,
    fetchLoading: !visible || fetchLoading,
    updateLoading,
    updatePlaning,
    handlePlaning,
    calculatePlaning,
    evaluatePlaning,
    cumulativeApaPoints: visible ? cumulativeApaPoints : 0,
    calculateLoading,
    evaluateLoading,
    status: visible ? status : 0,
    showEvaluate,
    semesterComments: visible ? semesterComments : null
  };
};

export default useAdminEvaluation;